import { useReducer, useEffect, useCallback, useRef } from 'react';
import { logger } from '../../../shared/lib/logger';
import { DEFAULT_SETTINGS, SETTINGS_STORAGE_KEY } from '../constants';
import type { SettingsState, SettingsAction, SettingValue } from '../types';

type SettingsReducer = (state: SettingsState, action: SettingsAction) => SettingsState;

const settingsReducer: SettingsReducer = (state, action) => {
    switch (action.type) {
        case 'UPDATE_SETTING': {
            const newState = { ...state, [action.payload.key]: action.payload.value };
            try {
                localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(newState));
            } catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                logger.error('Failed to save settings', { error: error.message, stack: error.stack });
            }
            return newState;
        }
        case 'RESET_TO_DEFAULTS':
            localStorage.removeItem(SETTINGS_STORAGE_KEY);
            return DEFAULT_SETTINGS;
        case 'LOAD_SETTINGS':
            return action.payload;
        default:
            return state;
    }
};

const createSettingsChannel = () => {
    if (typeof window === 'undefined') return null;
    try {
        return new BroadcastChannel('scanta-settings');
    } catch {
        return null;
    }
};

export const useSettings = () => {
    const [state, dispatch] = useReducer(settingsReducer, DEFAULT_SETTINGS);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const isInitializedRef = useRef(false);

    useEffect(() => {
        if (isInitializedRef.current) return;
        
        try {
            const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                dispatch({ type: 'LOAD_SETTINGS', payload: { ...DEFAULT_SETTINGS, ...parsed } });
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            logger.error('Failed to load settings', { error: error.message });
        }
        
        isInitializedRef.current = true;
    }, []);

    useEffect(() => {
        channelRef.current = createSettingsChannel();
        
        if (channelRef.current) {
            channelRef.current.onmessage = (event) => {
                if (event.data?.type === 'SETTINGS_UPDATED') {
                    dispatch({ type: 'LOAD_SETTINGS', payload: event.data.payload });
                }
            };
        }

        return () => {
            channelRef.current?.close();
        };
    }, []);

    const updateSetting = useCallback(<K extends keyof SettingsState>(
        key: K,
        value: SettingValue
    ) => {
        dispatch({ type: 'UPDATE_SETTING', payload: { key, value } });
        
        const newState = { ...state, [key]: value };
        channelRef.current?.postMessage({
            type: 'SETTINGS_UPDATED',
            payload: newState,
        });

        logger.info('Setting updated', { key, value: String(value) });
    }, [state]);

    const resetToDefaults = useCallback(() => {
        dispatch({ type: 'RESET_TO_DEFAULTS' });
        channelRef.current?.postMessage({
            type: 'SETTINGS_UPDATED',
            payload: DEFAULT_SETTINGS,
        });
        logger.info('Settings reset to defaults');
    }, []);

    const exportSettings = useCallback(() => {
        const dataStr = JSON.stringify(state, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `scanta-settings-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [state]);

    const importSettings = useCallback((file: File) => {
        return new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const parsed = JSON.parse(e.target?.result as string);
                    const validKeys = Object.keys(DEFAULT_SETTINGS);
                    const isValid = validKeys.every(key => key in parsed);
                    
                    if (!isValid) {
                        throw new Error('Invalid settings file structure');
                    }
                    
                    dispatch({ type: 'LOAD_SETTINGS', payload: parsed });
                    channelRef.current?.postMessage({
                        type: 'SETTINGS_UPDATED',
                        payload: parsed,
                    });
                    resolve();
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    }, []);

    return {
        state,
        updateSetting,
        resetToDefaults,
        exportSettings,
        importSettings,
    };
};